import Box from '@mui/material/Box';
import { Drawer } from '@openx/components-core/lib/Drawer/Drawer.tsx';
import { DrawerActionBar, type DrawerActions } from '@openx/components-core/lib/Drawer/DrawerActionBar.tsx';
import { DrawerHeader } from '@openx/components-core/lib/Drawer/DrawerHeader.tsx';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface MoreFiltersDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  onClear: () => void;
  isApplyDisabled?: boolean;
  isClearDisabled?: boolean;
  title?: string;
  width?: string | number;
  children?: ReactNode;
}

export const MoreFiltersDrawer = ({
  open,
  onClose,
  onApply,
  onClear,
  isApplyDisabled = false,
  isClearDisabled = false,
  title,
  width,
  children,
}: MoreFiltersDrawerProps) => {
  const { t } = useTranslation();

  const actions: DrawerActions = useMemo(
    () => [
      {
        color: 'primary',
        label: t('Apply'),
        onClick: onApply,
        disabled: isApplyDisabled,
        'data-test': 'more-filters-apply-button',
      },
      {
        label: t('Cancel'),
        onClick: onClose,
        variant: 'text',
        'data-test': 'more-filters-cancel-button',
      },
      {
        label: t('Clear All Filters'),
        onClick: onClear,
        variant: 'text',
        color: 'error',
        disabled: isClearDisabled,
        'data-test': 'more-filters-clear-button',
      },
    ],
    [isApplyDisabled, isClearDisabled, onApply, onClose, onClear, t]
  );

  const drawerSx = useMemo(
    () => ({
      '& .MuiDrawer-paper': {
        padding: 0,
        ...(width ? { width } : {}),
      },
    }),
    [width]
  );

  return (
    <Drawer open={open} onClose={onClose} sx={drawerSx} data-test="more-filters-drawer">
      <Box data-test="more-filters-drawer-content" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DrawerHeader onClose={onClose} sx={{ mb: 0, px: 3, pt: 1.5 }}>
          {title ?? t('More Filters')}
        </DrawerHeader>
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>{children}</Box>
        <DrawerActionBar actions={actions} stickyBottom sidePadding />
      </Box>
    </Drawer>
  );
};
